import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Grid, Paper, Typography, useTheme, IconButton, MenuItem, Menu } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import StarRateIcon from '@mui/icons-material/StarRate';

import { useJobs, TYPES as JOB_TYPES } from 'services/job';
import { cancelService, declineService } from 'services/actions/service';
import { useError } from 'contexts/ErrorContext';
import { useCompany } from 'services/company';

import ServiceDetails from './ServiceDetails';

import { ServiceReworkModal } from '../ServiceReworkModal';
import { ReviewModal } from './ReviewModal';

const RepairerCards = ({ activeJob, quickOptionsEnums }) => {
    const jobsContext = useJobs();
    const theme = useTheme();
    const err = useError();
    const companyContext = useCompany();
    const isRepairer = companyContext.state.company?.marketplace_side === 'Repairer';

    const [anchorElMenu, setAnchorElMenu] = useState(null);
    const [currentOpenMenu, setCurrentOpenMenu] = useState(0);
    const [reworkModalOpen, setRWModalOpen] = useState(false);
    const [selectedReworkService, setSelectedReworkService] = useState(null);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewModalCompany, setReviewModalCompany] = useState(null);
    const [rated, setRated] = useState({});

    const handleClickMenu = ((current) => {
        setAnchorElMenu(current);
    });

    const handleCloseMenu = (() => {
        setAnchorElMenu(null);
        setCurrentOpenMenu(0);
    });

    const handleCancelMenuItem = async function(option) {
        try {
            await Promise.all(option.payload.map(el => cancelService(el)));
            await jobsContext.DISPATCH({ type: JOB_TYPES.SET_ACTIVE_JOB, payload: activeJob.id });
            handleCloseMenu();
        } catch (error) {
            console.log(error);
            // @ts-ignore
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        }
    };

    const handleDeclineMenuItem = async function(option) {
        try {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: true });
            await Promise.all(option.payload.map(el => declineService(el)));
            await jobsContext.actions.refreshJob(activeJob.id);
            handleCloseMenu();
        } catch (error) {
            console.log(error);
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: false });
        }
    };

    const handleReworkMenuItem = async function(option) {
        setRWModalOpen(true);
        setSelectedReworkService(option);
    };

    const renderReworkModal = (option) => {
        return (
            reworkModalOpen && (
                <ServiceReworkModal
                    open={reworkModalOpen}
                    setOpen={setRWModalOpen}
                    serviceID={option.serviceID}
                    serviceDetailIDs={`${option.serviceDetailIDs}`}
                    serviceDetails={option.serviceDetails}
                    serviceMedia={['serviceMedia']}
                    serviceNotes={['serviceNotes']}
                    repairCatalog={['repairCatalog']}
                    DISPATCH={() => jobsContext.DISPATCH({ type: JOB_TYPES.SET_ACTIVE_JOB, payload: activeJob.id })}
                />
            )
        );
    };

    const renderReviewModal = () => {
        return reviewModalOpen && (
            <ReviewModal
                open={reviewModalOpen}
                jobID={activeJob.id}
                repairerCompany={reviewModalCompany}
                onClose={() => setReviewModalOpen(false)}
                onSubmit={handleReviewSubmitted}
            ></ReviewModal>
        );
    };

    const handleReviewSubmitted = async (result) => {
        setRated(prev => ({
            ...prev,
            [activeJob.id + '-' + result.repairerCompany.id]: result,
        }));

        // Refresh the job data to update stars in JobCard and JobDetails
        try {
            await jobsContext.actions.refreshJob(activeJob.id);
        } catch (error) {
            console.error('Error refreshing job after rating:', error);
        }
    };

    if(activeJob.reviews) {
        activeJob.reviews.forEach(review => {
            if(!rated[activeJob.id + '-' + review.reviewed_company.id]) {
                setRated(prev => ({
                    ...prev,
                    [activeJob.id + '-' + review.reviewed_company.id]: review,
                }));
            }
        });
    }

    const services = activeJob.services;
    const repairerList = Array.from(new Set(services.map(el => el.repair_company.name))).sort();
    const repairerElements = repairerList.map((repairerName, key) => {
        const rep = isRepairer ? (activeJob?.supplier_company?.name || repairerName) : repairerName;
        let serviceList = services.filter(el => el.repair_company.name === repairerName);
        let total = serviceList.reduce((sum, part) => sum += part.price, 0);
        let closeOpts = [];
        let reworkOpts = [];
        let declineOpts = [];

        if (isRepairer) {
            declineOpts.push({
                label: `Decline all ${rep} services`,
                payload: serviceList.filter(
                    el => quickOptionsEnums['DECLINABLE'].includes(el.service_status.toUpperCase().replace(/ /g, '_')),
                ).map(el => ({ serviceID: el.id })),
            });

            declineOpts.push(...serviceList.filter(
                el => quickOptionsEnums['DECLINABLE'].includes(el.service_status.toUpperCase().replace(/ /g, '_')),
            ).map(el => {
                return {
                    label: `Decline ${el.service_details.service_type} ` +
                        `${el.service_details.position ? el.service_details.position.replace('N/A', '') : ''} ` +
                        `${el.service_details.part ? el.service_details.part.replace('N/A', '') : ''}`,
                    payload: [{ serviceID: el.id }],
                };
            }));

            // Filter out options with empty payloads
            declineOpts = declineOpts.filter(opt => opt.payload.length > 0);
        } else {
            closeOpts.push({
                label: `Cancel all possible ${rep} services`,
                payload: serviceList.filter(
                    el => quickOptionsEnums['CANCELLABLE'].includes(el.service_status.toUpperCase()),
                ).map(el => ({ serviceID: el.id })),
            });

            closeOpts.push(...serviceList.filter(el => quickOptionsEnums['CANCELLABLE'].includes(el.service_status.toUpperCase())).map(el => {
                return {
                    label: `Cancel ${el.service_details.service_type} ` +
                        `${el.service_details.position ? el.service_details.position.replace('N/A', '') : ''} ` +
                        `${el.service_details.part ? el.service_details.part.replace('N/A', '') : ''}`,
                    payload: [{ serviceID: el.id }],
                };
            }));

            reworkOpts.push(...serviceList.filter(el => quickOptionsEnums['REWORKABLE'].includes(el.service_status.toUpperCase())).map(el => {
                return {
                    label: `Request rework on ${el.service_details.service_type} ` +
                        `${el.service_details.position ? el.service_details.position.replace('N/A', '') : ''} ` +
                        `${el.service_details.part ? el.service_details.part.replace('N/A', '') : ''}`,
                    serviceID: el.id,
                    serviceDetailIDs: el.service_details.id,
                    serviceDetails: `${el.service_details.service_type} ` +
                        `${el.service_details.position ? el.service_details.position.replace('N/A', '') : ''} ` +
                        `${el.service_details.part ? el.service_details.part.replace('N/A', '') : ''}`,
                    payload: [{ serviceID: el.id }],
                };
            }));
        }

        return (
            <Paper key={key} elevation={0} sx={{ backgroundColor: theme.palette.grey[50], width: '100%', p:1, my:2 }}>
                <Grid display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography sx={{ fontSize: 16, fontWeight: 400, ml: 0.5 }} color={theme.palette.grey[900]}> { rep } </Typography>
                    <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                        {!isRepairer && (!rated || !rated[activeJob.id + '-' + serviceList[0].repair_company.id]) && (
                            <>
                                <StarBorderOutlinedIcon fontSize='small' sx={{ color: theme.palette.blue[700], mr: 0.5 }} />
                                <Typography
                                    id={`rate-${serviceList[0].repair_company.id}`}
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 400,
                                        width: 119,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                    color={theme.palette.blue[700]}
                                    onClick={() => {
                                        setReviewModalCompany(serviceList[0].repair_company);
                                        setReviewModalOpen(true);
                                    }}
                                >
                                    Rate this vendor
                                </Typography>
                            </>
                        )}
                        {!isRepairer && rated && rated[activeJob.id + '-' + serviceList[0].repair_company.id] && (
                            <>
                                <Typography
                                    sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1 }}
                                    color={theme.palette.primary[700]}
                                >
                                    {rated[activeJob.id + '-' + serviceList[0].repair_company.id].rating} /{' '}
                                </Typography>
                                <StarRateIcon
                                    fontSize='small'
                                    sx={{ color: theme.palette.blue[700],lineHeight: 1.5, mr: 0.5 }}
                                />
                            </>
                        )}
                        <Typography
                            sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1 }}
                            color={theme.palette.grey[900]}
                        >
                            {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            }).format(total)}
                        </Typography>
                        <IconButton
                            onClick={(event) => {setCurrentOpenMenu(key); handleClickMenu(event.currentTarget);}}
                        >
                            <MoreVertIcon sx={{ color: theme.palette.grey[500] }} />
                        </IconButton>
                        <Menu
                            id={`long-menu-${rep}`}
                            anchorEl={anchorElMenu}
                            open={Boolean(anchorElMenu) && currentOpenMenu === key}
                            onClose={handleCloseMenu}
                        >
                            {declineOpts.map((option, j) => (
                                <MenuItem
                                    key={`decline-${j}`}
                                    onClick={() => handleDeclineMenuItem(option)}
                                    sx={{ '&:hover': { backgroundColor: theme.palette.blue[30] } }}
                                >
                                    { option.label }
                                </MenuItem>
                            ))}
                            {closeOpts.map((option, j) => (
                                <MenuItem
                                    key={`cancel-${j}`}
                                    onClick={() => handleCancelMenuItem(option)}
                                    sx={{ '&:hover': { backgroundColor: theme.palette.blue[30] } }}
                                >
                                    { option.label }
                                </MenuItem>
                            ))}
                            {reworkOpts.map((option, j) => (
                                <MenuItem
                                    key={`rework-${j}`}
                                    onClick={() => handleReworkMenuItem(option)}
                                    sx={{ '&:hover': { backgroundColor: theme.palette.blue[30] } }}
                                >
                                    { option.label }
                                </MenuItem>
                            ))}
                        </Menu>
                        { renderReworkModal(selectedReworkService) }
                        { renderReviewModal() }
                    </Box>
                </Grid>
                <Grid justifyContent={'space-evenly'} alignItems={'center'}>
                    {(() => {
                        return serviceList.map((service, i) => {
                            return <ServiceDetails
                                service={service}
                                key={i}
                                activeJob={activeJob}
                                rep={rep}
                            />;
                        });

                    })() }
                </Grid>
            </Paper>
        );
    });

    return (
        <Grid container display={'flex'} flexDirection={'column'} justifyContent={'center'} alignContent={'center'} px={2} my={1}>
            <Typography
                display={'flex'}
                alignSelf={'center'}
                my={1}
                sx={{ fontSize: 14, fontWeight: 500 }}
                color={theme.palette.grey[900]}
            >
                {isRepairer ? 'Your Services' : 'Services by Repairer'}
            </Typography>
            <Grid container display={'flex'} flexDirection={'row'} justifyContent={'center'} alignContent={'center'}>
                { repairerElements }
            </Grid>
        </Grid>
    );
};

RepairerCards.propTypes = {
    activeJob: PropTypes.object,
    quickOptionsEnums: PropTypes.object,
};

export default RepairerCards;
